import React, { useEffect, useMemo, useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";

// Summaries already fetched this session, keyed on the notes they cover, so
// re-opening a record does not wait on the network again.
const memo = new Map();

const pick = (n) => {
  if (!n) return null;
  if (typeof n === "string") return { body: n };
  const body = n.body ?? n.text ?? n.note ?? n.content ?? "";
  if (!String(body).trim()) return null;
  return {
    body: String(body),
    date: n.created_at || n.date || n.timestamp || "",
    author: n.author_name || n.author || n.created_by_name || n.agent_name || "",
  };
};

/**
 * The AI summary that sits under every "Agent Notes" heading, so an agent can
 * see where a record is at without reading every note (Christian Gravias,
 * Monday 13202770775, 5 Oct 2026).
 *
 * Pass the notes the panel already shows: `notes` as rows, or `text` as one
 * stamped blob ("[1/10/2026 1:01 pm] (Simon Abbott) ..."). Renders nothing when
 * there are no notes, and quietly disappears if the summary is unavailable, so
 * the notes themselves are never held up.
 */
export default function AgentNotesSummary({ notes, text, subject, className = "" }) {
  const rows = useMemo(() => (Array.isArray(notes) ? notes.map(pick).filter(Boolean) : []), [notes]);
  const blob = typeof text === "string" ? text.trim() : "";
  const key = useMemo(
    () => (rows.length || blob ? JSON.stringify([rows, blob, subject || ""]) : ""),
    [rows, blob, subject]
  );

  const [state, setState] = useState(() => (key && memo.has(key) ? { summary: memo.get(key) } : null));

  useEffect(() => {
    if (!key) { setState(null); return undefined; }
    if (memo.has(key)) { setState({ summary: memo.get(key) }); return undefined; }

    let cancelled = false;
    setState({ loading: true });
    const timer = setTimeout(() => {
      // Loaded on demand so the many panels (and their tests) that render this
      // box do not all pull the API client in at import time.
      import("../../api/notesSummary")
        .then(({ getNotesSummary }) =>
          getNotesSummary({ notes: rows.length ? rows : undefined, text: blob || undefined, subject })
        )
        .then((data) => {
          if (cancelled) return;
          const summary = data?.summary || null;
          if (summary) memo.set(key, summary);
          setState(summary ? { summary } : null);
        })
        .catch(() => { if (!cancelled) setState(null); });
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
    // `key` covers rows, blob and subject.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (!key || !state) return null;

  return (
    <div
      className={`rounded-lg border border-[#55c5d0]/40 bg-[#eefafb] px-3 py-2.5 ${className}`}
      data-testid="agent-notes-summary"
    >
      <div className="flex items-center gap-1.5 text-[10.5px] font-extrabold uppercase tracking-wider text-[#2a9aa5]">
        <Sparkles size={12} /> AI summary
      </div>
      {state.loading ? (
        <div className="mt-1 flex items-center gap-1.5 text-xs text-gray-500">
          <Loader2 size={12} className="animate-spin" /> Summarising the notes...
        </div>
      ) : (
        <p className="mt-1 text-[12.8px] leading-relaxed text-gray-800 whitespace-pre-line">{state.summary}</p>
      )}
    </div>
  );
}
